import { useEffect, useState, useRef } from 'react';
import {
  FileText,
  FileImage,
  FileType,
  File as FileIcon,
  Trash2,
  Download,
  Upload,
  X,
  Loader2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import {
  listPropertyFiles,
  uploadPropertyFile,
  deletePropertyFile,
  downloadPropertyFileUrl,
} from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';

export const CATEGORY_LABELS = {
  house_info: 'House Info',
  house_rules: 'House Rules',
  wifi_access: 'Wi-Fi & Access',
  cleaning_guide: 'Cleaning Guide',
  maintenance_guide: 'Maintenance Guide',
  emergency_contacts: 'Emergency Contacts',
  other: 'Other',
};

const CATEGORY_TONE = {
  house_info: 'bg-blue-100 text-blue-800',
  house_rules: 'bg-rose-100 text-rose-800',
  wifi_access: 'bg-sky-100 text-sky-800',
  cleaning_guide: 'bg-emerald-100 text-emerald-800',
  maintenance_guide: 'bg-amber-100 text-amber-800',
  emergency_contacts: 'bg-red-100 text-red-800',
  other: 'bg-slate-100 text-slate-800',
};

const ACCEPT_MIMES =
  '.pdf,.doc,.docx,.jpg,.jpeg,.png,.txt,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/jpeg,image/png,text/plain';

const MAX_BYTES = 25 * 1024 * 1024;

const iconForType = (contentType, size = 'h-5 w-5') => {
  if (!contentType) return <FileIcon className={size} />;
  if (contentType.startsWith('image/')) return <FileImage className={size} />;
  if (contentType.includes('pdf')) return <FileType className={size} />;
  if (contentType.includes('word')) return <FileText className={size} />;
  if (contentType === 'text/plain') return <FileText className={size} />;
  return <FileIcon className={size} />;
};

const formatBytes = (n) => {
  if (!n) return '0 B';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

export const PropertyFilesPanel = ({ propertyId }) => {
  const { user } = useAuth();
  const canDelete = ['property_manager', 'owner'].includes(user?.role);

  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [category, setCategory] = useState('house_info');
  const [description, setDescription] = useState('');
  const inputRef = useRef(null);

  const refresh = async () => {
    if (!propertyId) return;
    try {
      setLoading(true);
      const { data } = await listPropertyFiles(propertyId);
      setFiles(data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load files');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, [propertyId]);

  const handlePick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > MAX_BYTES) {
      toast.error('File exceeds 25 MB limit');
      e.target.value = '';
      return;
    }
    setPendingFile(f);
  };

  const handleUpload = async () => {
    if (!pendingFile) {
      toast.error('Choose a file first');
      return;
    }
    try {
      setUploading(true);
      await uploadPropertyFile(propertyId, pendingFile, category, description);
      toast.success(`Uploaded ${pendingFile.name}`);
      setPendingFile(null);
      setDescription('');
      if (inputRef.current) inputRef.current.value = '';
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (fileId, filename) => {
    if (!window.confirm(`Delete ${filename}? This cannot be undone.`)) return;
    try {
      await deletePropertyFile(propertyId, fileId);
      toast.success('File deleted');
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div className="space-y-5" data-testid="property-files-panel">
      {/* UPLOAD FORM */}
      <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
        <p className="text-sm font-medium text-slate-800 mb-3 flex items-center gap-2">
          <Upload className="h-4 w-4" />
          Upload a file
        </p>
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <Label htmlFor="file-input" className="text-xs">
              File (PDF, DOCX, JPG, PNG, TXT · max 25 MB)
            </Label>
            <Input
              id="file-input"
              ref={inputRef}
              type="file"
              accept={ACCEPT_MIMES}
              onChange={handlePick}
              className="cursor-pointer mt-1"
              data-testid="file-picker-input"
            />
            {pendingFile && (
              <p className="text-xs text-slate-500 mt-1">
                Selected: <span className="font-medium">{pendingFile.name}</span>{' '}
                ({formatBytes(pendingFile.size)})
              </p>
            )}
          </div>
          <div>
            <Label htmlFor="category-select" className="text-xs">
              Category
            </Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger
                id="category-select"
                className="mt-1"
                data-testid="file-category-select"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(CATEGORY_LABELS).map(([val, label]) => (
                  <SelectItem key={val} value={val}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2">
            <Label htmlFor="file-description" className="text-xs">
              Description (optional)
            </Label>
            <Textarea
              id="file-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What's in this file? e.g. 'Master bedroom Wi-Fi + streaming logins'"
              rows={2}
              className="mt-1 resize-none"
              data-testid="file-description-input"
            />
          </div>
        </div>
        <div className="mt-3 flex justify-end gap-2">
          {pendingFile && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                setPendingFile(null);
                if (inputRef.current) inputRef.current.value = '';
              }}
            >
              <X className="h-4 w-4 mr-1" />
              Clear
            </Button>
          )}
          <Button
            type="button"
            onClick={handleUpload}
            disabled={!pendingFile || uploading}
            data-testid="file-upload-btn"
          >
            {uploading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Uploading…
              </>
            ) : (
              <>
                <Upload className="h-4 w-4 mr-2" />
                Upload
              </>
            )}
          </Button>
        </div>
      </div>

      {/* FILE LIST */}
      <div>
        <p className="text-sm font-medium text-slate-800 mb-2">
          {loading ? 'Loading…' : `Files (${files.length})`}
        </p>
        {!loading && files.length === 0 && (
          <div className="rounded-lg border border-dashed border-slate-300 p-6 text-center text-sm text-slate-500">
            No files yet. Upload house info, rules, Wi-Fi codes, or any doc your
            team needs on-site.
          </div>
        )}
        {files.length > 0 && (
          <ul className="divide-y divide-slate-200 rounded-lg border border-slate-200 bg-white">
            {files.map((f) => (
              <li
                key={f.id}
                className="flex items-center gap-3 p-3 hover:bg-slate-50 transition-colors"
                data-testid={`file-row-${f.id}`}
              >
                <div className="w-9 h-9 rounded-lg bg-slate-100 text-slate-600 flex items-center justify-center flex-shrink-0">
                  {iconForType(f.content_type)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-medium text-slate-900 truncate">
                      {f.filename}
                    </p>
                    <Badge
                      className={`${CATEGORY_TONE[f.category] || CATEGORY_TONE.other} border-transparent text-xs`}
                    >
                      {CATEGORY_LABELS[f.category] || 'Other'}
                    </Badge>
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {formatBytes(f.size_bytes)} ·{' '}
                    {f.uploaded_by_name || 'Someone'} ·{' '}
                    {(() => {
                      try {
                        return format(parseISO(f.created_at), 'PP');
                      } catch {
                        return '';
                      }
                    })()}
                  </p>
                  {f.description && (
                    <p className="text-xs text-slate-600 mt-1 italic">
                      {f.description}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <a
                    href={downloadPropertyFileUrl(propertyId, f.id)}
                    className="inline-flex items-center justify-center h-8 w-8 rounded-md text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition-colors"
                    title="Download"
                    data-testid={`file-download-${f.id}`}
                  >
                    <Download className="h-4 w-4" />
                  </a>
                  {canDelete && (
                    <button
                      type="button"
                      onClick={() => handleDelete(f.id, f.filename)}
                      className="inline-flex items-center justify-center h-8 w-8 rounded-md text-red-600 hover:bg-red-50 transition-colors"
                      title="Delete"
                      data-testid={`file-delete-${f.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default PropertyFilesPanel;
